import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { GiftTtlField } from "@/components/brand/settings/products/gift-ttl-field"
import { brandSettingsProductGiftTtlPath } from "@/routes"
import type { ProductRow } from "@/types/products"

/**
 * OKS-935 S2 — the product-level gift TTL editor (AC1).
 *
 * Extracted rather than inlined because it renders on BOTH the desktop table
 * and the mobile card grid. Those two surfaces have drifted before (the
 * `variant_count` bug in OKS-932 hit both and was fixed once, in the
 * serializer, for exactly this reason) — one component means the endpoint, the
 * commit rule and the error key cannot disagree between breakpoints.
 *
 * # Why a product-level TTL exists at all
 *
 * It is the tail of `COALESCE(mission_product, product_variant, product)`, and
 * it is the UNION for every variant row the Produits table structurally cannot
 * show: other pagination pages, and variants filtered out by
 * `serialize_product`'s `archived? == archived_view` rule. Those rows are
 * untouched by any save and inherit this value rather than silently getting
 * none (PRD D16). An archived variant under an ACTIVE product is editable
 * nowhere, and this is what covers it (OQ-7).
 *
 * # ⚠️ Not `brand_settings_products#update`
 *
 * That action refuses non-manual products, and a SYNCED product is the primary
 * TTL subject (OQ-6) — it has no form screen at all. `gift_ttl_days` is an
 * overlay column the sync never names, so it survives a resync by
 * construction.
 */
export function ProductGiftTtlForm({
  product,
  errors,
}: {
  product: ProductRow
  errors?: Record<string, string | string[] | undefined>
}) {
  const { t } = useTranslation()

  return (
    <Form
      action={brandSettingsProductGiftTtlPath(product.id)}
      method="patch"
      options={{ preserveScroll: true }}
      className="mt-2"
      data-testid={`brand-product-ttl-form-${product.id}`}
    >
      {({ submit }) => (
        <GiftTtlField
          id={`product-ttl-${product.id}`}
          name="gift_ttl[gift_ttl_days]"
          value={product.gift_ttl_days}
          error={errors?.gift_ttl_days}
          label={t("settings.brand.products.ttl.product_label")}
          testId={`brand-product-ttl-${product.id}`}
          // Commit on blur, and only when the value actually changed — a bare
          // blur handler fires a PATCH on every focus traversal.
          onCommit={(event) => {
            const next = event.currentTarget.value.trim()
            const current =
              product.gift_ttl_days === null
                ? ""
                : String(product.gift_ttl_days)
            if (next !== current) submit()
          }}
        />
      )}
    </Form>
  )
}
